<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { AddressFinder } from '@ideal-postcodes/address-finder';
import type { AddressSelectedPayload } from '../types/ideal-postcodes';
import { COUNTRY_CODE } from '@/utils/constants';
import { toAddressSelectedPayload, toManualEntryPayload } from '../utils/ideal-postcodes-address';
import { isIdealPostcodesFeatureEnabled } from '../utils/ideal-postcodes-config';

type AddressFinderInstance = ReturnType<typeof AddressFinder.setup>;

const IDPC_QUERY_TAGS = [`env:${import.meta.env.VITE_APP_ENV}`, 'solcentre:query'].join(',');
const IDPC_RESOLVE_TAGS = [`env:${import.meta.env.VITE_APP_ENV}`, 'solcentre:resolve'].join(',');

const props = defineProps<{
  inputFieldId: string;
  value?: string;
  projectCountry?: string | null;
  postcode?: string | null;
  fullAddress?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:value', value: string): void;
  (e: 'address-selected', payload: AddressSelectedPayload): void;
}>();

const idpcEnabled = isIdealPostcodesFeatureEnabled();

const addressFinder = ref<AddressFinderInstance | null>(null);
const afAttached = ref(false);
const isManualMode = ref(false);
const skipNextFocus = ref(false);
const inputWithListeners = ref<HTMLInputElement | null>(null);
const manualEntryId = `idpc-manual-entry-${props.inputFieldId}`;
const MAX_INPUT_LOOKUP_FRAMES = 10;

/**
 * Find the actual input element. This is needed because antdv sometimes wraps the input in other elements.
 */
function getInputElement(): HTMLInputElement | null {
  const hostElement = document.getElementById(props.inputFieldId);
  if (!hostElement) {
    return null;
  }

  if (hostElement instanceof HTMLInputElement) {
    return hostElement;
  }

  return hostElement.querySelector('input[type="text"]');
}

function restoreInputFocusNextFrame() {
  requestAnimationFrame(() => {
    getInputElement()?.focus();
  });
}

function reattachFinder() {
  if (!addressFinder.value || afAttached.value) {
    return;
  }

  addressFinder.value.attach();
  afAttached.value = true;
  isManualMode.value = false;
  if (!getInputElement()?.focus()) {
    restoreInputFocusNextFrame();
  }
}

function detachFinder() {
  if (!addressFinder.value) {
    return;
  }

  addressFinder.value.detach();
  afAttached.value = false;
}

function handleInputFocus() {
  ensureAddressFinderReady();
  if (skipNextFocus.value) {
    skipNextFocus.value = false;
    return;
  }
  if (isManualMode.value) {
    reattachFinder();
  }
}

function enterManualMode() {
  detachFinder();
  isManualMode.value = true;
  skipNextFocus.value = true; //  So that the restored focus doesn't re-attach address finder
  restoreInputFocusNextFrame();

  emit('address-selected', toManualEntryPayload(props.value, props.fullAddress ?? false));
}

watch(
  () => props.value,
  (newVal) => {
    if (isManualMode.value && newVal === '') {
      reattachFinder();
    }
  },
);

function injectManualEntryOption() {
  const input = getInputElement();
  if (!input) {
    return;
  }

  const root = input.closest('.idpc_autocomplete') || input.parentElement || document.body;
  const suggestionsContainer = root.querySelector('.idpc_ul') || document.querySelector('.idpc_ul');
  if (!suggestionsContainer) {
    return;
  }

  let manualEntry = document.getElementById(manualEntryId);
  if (!manualEntry) {
    manualEntry = document.createElement('div');
    manualEntry.id = manualEntryId;
    manualEntry.className = 'idpc_manual_entry';
    manualEntry.textContent = 'Manual entry';
    manualEntry.style.cssText = `
      padding: 10px;
      cursor: pointer;
      font-weight: bold;
      background: #f6f6f6;
    `;
    manualEntry.addEventListener('click', (event) => {
      event.preventDefault();
      enterManualMode();
    });
  }

  suggestionsContainer.after(manualEntry);
}

function resolveCountrySettings(country?: string | null) {
  if (!country) {
    return {};
  }

  const COUNTRY_CODE_ISO3: Record<string, string> = {
    [COUNTRY_CODE['United Kingdom']]: 'GBR',
    [COUNTRY_CODE.Australia]: 'AUS',
    [COUNTRY_CODE['United States']]: 'USA',
    [COUNTRY_CODE['New Zealand']]: 'NZL',
    [COUNTRY_CODE.Germany]: 'DEU',
  };
  const defaultCountry = COUNTRY_CODE_ISO3[country] ?? null;
  return defaultCountry ? { detectCountry: false, defaultCountry } : {};
}

function resolvePostcodeOption(postcode?: string | null) {
  if (!postcode) {
    return undefined;
  }
  const trimmedPostcode = postcode.trim();
  return trimmedPostcode ? { postcode: trimmedPostcode } : undefined;
}

function setupAddressFinder(): AddressFinderInstance {
  const postcodeOption = resolvePostcodeOption(props.postcode);
  const queryOptions = { tags: IDPC_QUERY_TAGS, ...postcodeOption };
  const resolveOptions = { tags: IDPC_RESOLVE_TAGS };
  const countrySettings = resolveCountrySettings(props.projectCountry);

  return AddressFinder.setup({
    apiKey: import.meta.env.VITE_IDEAL_POSTCODES_API_KEY,
    inputField: `#${props.inputFieldId}`,
    queryOptions,
    resolveOptions,
    ...countrySettings,
    onMounted: () => (afAttached.value = true),
    onOpen: () => injectManualEntryOption(),
    onAddressRetrieved: (address) => {
      const payload = toAddressSelectedPayload(address, props.fullAddress ?? false);
      const nextValue = props.fullAddress ? payload.fullAddress ?? payload.line1 : payload.line1;
      emit('update:value', nextValue);
      emit('address-selected', payload);
    },
  });
}

function ensureAddressFinderReady() {
  if (!idpcEnabled || addressFinder.value) {
    return;
  }

  try {
    addressFinder.value = setupAddressFinder();
  } catch (error) {
    console.error('Error setting up Ideal Postcodes Autocomplete:', error);
  }
}

function initializeFinderWhenInputReady(frame = 0) {
  if (!idpcEnabled || addressFinder.value) {
    return;
  }

  const inputElement = getInputElement();
  if (inputElement) {
    if (inputWithListeners.value !== inputElement) {
      inputWithListeners.value?.removeEventListener('focus', handleInputFocus);
      inputElement.addEventListener('focus', handleInputFocus);
      inputWithListeners.value = inputElement;
    }
    // Once the input element is found, initialize the address finder and return.
    ensureAddressFinderReady();
    return;
  }

  // Retry up to 10 frames to find the input element. Else, give up.
  if (frame < MAX_INPUT_LOOKUP_FRAMES) {
    requestAnimationFrame(() => initializeFinderWhenInputReady(frame + 1));
  }
}

const handleInputUpdate = (value: string) => {
  emit('update:value', value);
};

onMounted(() => {
  if (!idpcEnabled) {
    console.log('Ideal Postcodes Autocomplete is not enabled');
    return;
  }

  initializeFinderWhenInputReady();
});

onBeforeUnmount(() => {
  inputWithListeners.value?.removeEventListener('focus', handleInputFocus);
  inputWithListeners.value = null;
  detachFinder();
  document.getElementById(manualEntryId)?.remove();
});
</script>

<template>
  <a-input
    :id="inputFieldId"
    :value="value"
    v-bind="$attrs"
    @update:value="handleInputUpdate"
  ></a-input>
</template>

<style lang="less">
// Global scope to override idpc styling
.idpc_autocomplete {
  display: flex;
  flex: 1;
}

.idpc_af {
  top: 100%;
}
</style>
